import React, { useEffect, useState } from "react";
import {
  Brain, Instagram, Facebook, Linkedin, Twitter, Youtube,
  MessageSquare, Image, BarChart3, Sparkles, Globe,
} from "lucide-react";

/* Decorative floating-AI hero composition — pure CSS + SVG, no images.
 * Layers:
 *  - Soft radial-glow background
 *  - Animated SVG neural-network with flowing data lines
 *  - Rotating outer & inner rings
 *  - Central pulsing AI brain
 *  - 6 orbiting marketing channel icons (Instagram, FB, LinkedIn, X, YouTube, Globe)
 *  - 4 floating capability badges
 *  - Particle dots rising in the background
 */

const ORBIT_ICONS = [
  { icon: Instagram, color: "#E1306C", delay: 0 },
  { icon: Facebook, color: "#1877F2", delay: 2.3 },
  { icon: Linkedin, color: "#0A66C2", delay: 4.6 },
  { icon: Twitter, color: "#1DA1F2", delay: 7 },
  { icon: Youtube, color: "#FF0000", delay: 9.3 },
  { icon: Globe, color: "#10B981", delay: 11.6 },
];

const BADGES = [
  { icon: Sparkles, text: "+30 posts", top: "8%", left: "-4%", delay: "0s" },
  { icon: Image, text: "Logo created", top: "22%", right: "-6%", delay: "1.5s" },
  { icon: MessageSquare, text: "Auto-reply sent", bottom: "18%", left: "-2%", delay: "3s" },
  { icon: BarChart3, text: "+24% engagement", bottom: "4%", right: "-4%", delay: "4.5s" },
];

export default function AIHeroVisual() {
  const [now, setNow] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setNow((n) => n + 1), 100);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="relative w-full aspect-square max-w-[520px] mx-auto select-none" aria-hidden>
      {/* Radial glow background */}
      <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.35)_0%,rgba(139,92,246,0.15)_30%,transparent_65%)] blur-2xl" />

      {/* Rising particles */}
      {Array.from({ length: 18 }).map((_, i) => (
        <span
          key={i}
          className="particle absolute h-1 w-1 rounded-full bg-indigo-300/70"
          style={{
            left: `${5 + (i * 53) % 95}%`,
            bottom: "0",
            animationDelay: `${(i * 0.45) % 6}s`,
            animationDuration: `${5 + (i % 4)}s`,
          }}
        />
      ))}

      {/* Outer rotating ring */}
      <div className="absolute inset-[6%] rounded-full border border-indigo-500/20 animate-spin-slower" />
      {/* Middle ring */}
      <div className="absolute inset-[16%] rounded-full border border-dashed border-violet-500/30 animate-spin-slow" />
      {/* Inner ring */}
      <div className="absolute inset-[28%] rounded-full border border-fuchsia-500/20 animate-spin-slower" />

      {/* SVG neural-network background */}
      <svg viewBox="0 0 100 100" className="absolute inset-0 w-full h-full text-indigo-400/40 data-flow">
        <defs>
          <linearGradient id="lineGrad" x1="0" x2="1" y1="0" y2="1">
            <stop offset="0%" stopColor="#6366F1" stopOpacity="0.7" />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.2" />
          </linearGradient>
        </defs>
        <g stroke="url(#lineGrad)" strokeWidth="0.3" fill="none">
          <path d="M50 50 L20 20" />
          <path d="M50 50 L80 20" />
          <path d="M50 50 L20 80" />
          <path d="M50 50 L80 80" />
          <path d="M50 50 L50 10" />
          <path d="M50 50 L50 90" />
          <path d="M50 50 L10 50" />
          <path d="M50 50 L90 50" />
        </g>
        {/* Network dots */}
        {[
          [20, 20], [80, 20], [20, 80], [80, 80],
          [50, 10], [50, 90], [10, 50], [90, 50],
        ].map(([x, y], i) => (
          <circle key={i} cx={x} cy={y} r="1.4" fill="#8B5CF6">
            <animate attributeName="r" values="1.4;2.4;1.4" dur="3s" repeatCount="indefinite" begin={`${i * 0.3}s`} />
            <animate attributeName="opacity" values="0.4;1;0.4" dur="3s" repeatCount="indefinite" begin={`${i * 0.3}s`} />
          </circle>
        ))}
      </svg>

      {/* Orbiting channel icons — wrappers are absolute centered, then each child orbits */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <div className="relative h-1 w-1">
          {ORBIT_ICONS.map(({ icon: Icon, color, delay }, i) => (
            <div
              key={i}
              className="absolute -translate-x-1/2 -translate-y-1/2 animate-orbit"
              style={{
                "--orbit-r": "165px",
                "--orbit-d": "22s",
                animationDelay: `-${delay}s`,
              }}
            >
              <div
                className="h-12 w-12 rounded-2xl glass-strong grid place-items-center shadow-xl backdrop-blur-lg"
                style={{ boxShadow: `0 0 24px -6px ${color}80, 0 0 0 1px ${color}40` }}
              >
                <Icon className="h-5 w-5" style={{ color }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Inner orbit — smaller dots */}
      <div className="absolute inset-0 grid place-items-center pointer-events-none">
        <div className="relative h-1 w-1">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="absolute -translate-x-1/2 -translate-y-1/2 animate-orbit-reverse"
              style={{
                "--orbit-r": "95px",
                "--orbit-d": "12s",
                animationDelay: `-${i * 3}s`,
              }}
            >
              <div className="h-2.5 w-2.5 rounded-full bg-indigo-400 shadow-[0_0_18px_rgba(99,102,241,0.9)]" />
            </div>
          ))}
        </div>
      </div>

      {/* Central AI brain */}
      <div className="absolute inset-0 grid place-items-center">
        <div className="relative">
          <div className="absolute -inset-6 rounded-full bg-gradient-to-br from-indigo-500/40 to-violet-500/40 blur-2xl animate-ai-pulse" />
          <div className="relative h-32 w-32 rounded-3xl brand-gradient grid place-items-center shadow-[0_0_60px_-8px_rgba(99,102,241,0.7)] animate-ai-pulse">
            <Brain className="h-14 w-14 text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.6)]" />
            {/* spinning halo */}
            <div className="absolute inset-[-8px] rounded-3xl border-2 border-white/30 border-dashed animate-spin-slow" />
          </div>
          <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full glass-strong text-[10px] uppercase tracking-[0.25em] text-white/80 font-mono whitespace-nowrap">
            AI brain · LIVE
          </div>
        </div>
      </div>

      {/* Floating capability badges */}
      {BADGES.map((b, i) => {
        const Icon = b.icon;
        const pos = {};
        if (b.top !== undefined) pos.top = b.top;
        if (b.bottom !== undefined) pos.bottom = b.bottom;
        if (b.left !== undefined) pos.left = b.left;
        if (b.right !== undefined) pos.right = b.right;
        return (
          <div
            key={i}
            className="absolute glass-strong rounded-xl px-3 py-2 text-xs shadow-2xl animate-drift backdrop-blur-lg border border-white/10"
            style={{ ...pos, animationDelay: b.delay }}
            data-testid={`hero-badge-${i}`}
          >
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg brand-gradient grid place-items-center">
                <Icon className="h-3.5 w-3.5 text-white" />
              </div>
              <span className="font-semibold text-white/90 whitespace-nowrap">{b.text}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
